import { useCallback, useMemo, useState } from 'react';

import type { UiDetailFieldSpec, UiEntitySpec } from '../config/uiSpec';
import { useEntityDetail } from '../data/useEntityDetail';
import type { UseEntityDetailResult } from '../data/useEntityDetail';
import { useEntityDelete } from '../data/useEntityDelete';
import { useUpdateEntity } from '../data/useUpdateEntity';
import { Card } from '../ui/Card';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';

export interface EntityDetailScreenProps {
  entity: UiEntitySpec;
  id: string | number;
  titleOverride?: string;
  onBack?: () => void;
  onDeleted?: () => void;
}

function formatFieldValue(
  field: UiDetailFieldSpec,
  record: Record<string, unknown>
): string {
  const raw = record[field.field];

  if (raw == null) {
    return '';
  }

  return String(raw);
}

export function EntityDetailScreen(
  props: EntityDetailScreenProps
): JSX.Element {
  const { entity, id, titleOverride, onBack, onDeleted } = props;

  const detailQuery: UseEntityDetailResult<Record<string, unknown>> =
    useEntityDetail<Record<string, unknown>>({
      entity: entity.entityName,
      id,
      enabled: true,
    });

  const { data, isLoading, isError, error } = detailQuery;

  const deleteMutation = useEntityDelete({
    entity: entity.entityName,
    id,
    onSuccess: () => {
      onBack?.();
      onDeleted?.();
    },
  });

  const title = titleOverride ?? entity.label;
  const detail = useMemo(() => entity.detail, [entity.detail]);

  const isRequirement = entity.entityName === 'Requirement';

  const workflowMutation = useUpdateEntity<Record<string, unknown>>({
    entity: entity.entityName,
    id,
  });

  const [actionError, setActionError] = useState<string | null>(null);

  const runWorkflowAction = useCallback(
    async (updates: Record<string, unknown>) => {
      setActionError(null);

      const result = await workflowMutation.update(updates);
      if (result === undefined) {
        setActionError(
          workflowMutation.error instanceof Error
            ? workflowMutation.error.message
            : 'Failed to update record'
        );
        return;
      }

      detailQuery.refetch();
    },
    [detailQuery, workflowMutation]
  );

  const handleDelete = useCallback(() => {
    const confirmed = window.confirm('Are you sure you want to delete this item?');
    if (!confirmed) return;

    void deleteMutation.mutate();
  }, [deleteMutation]);

  const headerActions = (
    <button
      type="button"
      className="button button-secondary button-danger"
      onClick={handleDelete}
      disabled={deleteMutation.isLoading}
    >
      {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
    </button>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={title} onBack={onBack} actions={headerActions} />
        <Card>
          <span className="text-sm text-slate-600">Loading…</span>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={title} onBack={onBack} actions={headerActions} />
        <Card>
          <span className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load record'}
          </span>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={title} onBack={onBack} actions={headerActions} />
        <EmptyState
          title="Record not found"
          description="The requested record could not be found."
        />
      </div>
    );
  }

  if (!detail || !detail.sections?.length) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={title} onBack={onBack} actions={headerActions} />
        <Card>
          <span className="text-sm text-slate-600">
            No detail layout has been configured for this entity.
          </span>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={title} onBack={onBack} actions={headerActions} />

      {isRequirement ? (
        <Card className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-slate-700">Workflow actions</h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="button button-secondary"
              disabled={
                workflowMutation.isSubmitting || data?.reviewStatus === 'approved'
              }
              onClick={() => runWorkflowAction({ reviewStatus: 'approved' })}
            >
              {workflowMutation.isSubmitting && data?.reviewStatus !== 'approved'
                ? 'Updating…'
                : 'Mark as Approved'}
            </button>
            <button
              type="button"
              className="button button-secondary"
              disabled={
                workflowMutation.isSubmitting || data?.reviewStatus === 'rejected'
              }
              onClick={() => runWorkflowAction({ reviewStatus: 'rejected' })}
            >
              {workflowMutation.isSubmitting && data?.reviewStatus !== 'rejected'
                ? 'Updating…'
                : 'Mark as Rejected'}
            </button>
            <button
              type="button"
              className="button button-primary"
              disabled={
                workflowMutation.isSubmitting || data?.generationRequested === true
              }
              onClick={() =>
                runWorkflowAction({
                  generationRequested: true,
                  generationStatus: 'pending',
                })
              }
            >
              {workflowMutation.isSubmitting && data?.generationRequested !== true
                ? 'Updating…'
                : 'Queue for Generation'}
            </button>
          </div>
          {actionError ? (
            <span className="text-sm text-red-600">{actionError}</span>
          ) : null}
        </Card>
      ) : null}

      {detail.sections.map((section) => (
        <Card key={section.id ?? section.label} className="flex flex-col gap-2">
          {section.label && (
            <h2 className="text-sm font-semibold text-slate-700">
              {section.label}
            </h2>
          )}

          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {section.fields.map((field) => (
              <div key={field.field} className="flex flex-col">
                <dt className="text-xs font-medium text-slate-500">
                  {field.label}
                </dt>
                <dd
                  className={`text-sm text-slate-900${
                    field.emphasize ? ' font-semibold' : ''
                  }`}
                >
                  {formatFieldValue(field, data)}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      ))}
      {deleteMutation.isError ? (
        <span className="text-sm text-red-600">
          {deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : 'Failed to delete record'}
        </span>
      ) : null}
    </div>
  );
}
